import { z } from 'zod';

/**
 * Decision-class questions extracted from LMP messages by the inquiry-workflow
 * pipeline (`packages/inquiry-workflow/src/activities/finalize.ts`). The
 * pipeline writes the `ExtractedQuestionSummary` payload onto Twilio message
 * attributes and merges it into `conversation_messages.attributes` JSONB on
 * the same write.
 *
 * `questionType` is intentionally typed as `string` rather than a closed enum
 * so that server-side enum additions never break the client. UI formats the
 * value uniformly (snake_case → Sentence case) without per-type lookups.
 *
 * When extraction is skipped (non-LMP, non-question, e2e, policy disabled)
 * the attribute is absent. UIs MUST treat missing as "no questions extracted"
 * (not "empty array").
 */

export const extractedQuestionSchema = z.object({
  /** Normalized decision-class question. Render this in the expanded card —
   * never the raw `sourceText`. */
  directQuestion: z.string(),
  /** Server-supplied type tag (e.g. `treatment_selection`, `biomarker_testing`).
   * Typed as `string` so unknown future values still render. */
  questionType: z.string(),
  /** DB invariant: exactly one row per message has `isPrimary === true`. */
  isPrimary: z.boolean(),
  /** Verbatim quote of the question span. `null` for implicit questions or
   * when the quote was missing/non-unique. */
  sourceText: z.string().nullable(),
  /** 0-based inclusive char offset into the message body. `null` if the quote
   * could not be unambiguously located. */
  sourceStartIndex: z.number().nullable(),
  /** 0-based exclusive char offset into the message body. `null` if the quote
   * could not be unambiguously located. */
  sourceEndIndex: z.number().nullable(),
});

export type ExtractedQuestion = z.infer<typeof extractedQuestionSchema>;

export const extractedQuestionSummarySchema = z.object({
  /** Length 1–5, ordered by `question_index ASC`. */
  extractedQuestions: z.array(extractedQuestionSchema),
  /** Index into `extractedQuestions`. `-1` if no primary (defensive — the
   * server invariant should ensure this never occurs in practice). */
  primaryQuestionIndex: z.number(),
});

export type ExtractedQuestionSummary = z.infer<typeof extractedQuestionSummarySchema>;

/**
 * Formats a server-supplied `questionType` for display.
 * Accepts any string; converts snake_case to Sentence case.
 *
 * @example
 * formatQuestionType('treatment_selection') // → 'Treatment selection'
 * formatQuestionType('made_up_value')       // → 'Made up value'
 * formatQuestionType('')                    // → ''
 */
export function formatQuestionType(value: string): string {
  if (!value) {
    return '';
  }

  const spaced = value.replace(/_+/g, ' ').trim();

  if (spaced.length === 0) {
    return '';
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
